{
  "manual": {
    "helpUrl": "https://help.family-album.com/hc/en-us/categories/52544526505497",
    "supportEmail": "global-support@family-album.com",
    "title": "User Manual",
    "headerTitle": "User Manual",
    "hero": {
      "welcome": "Welcome to the FamilyAlbum™\nDigital Frame",
      "description": "Thank you for choosing the FamilyAlbum™ Digital Frame.\nThis frame brings your family's memories to life by automatically syncing photos and videos from your FamilyAlbum™ app. Whether you're gifting it or enjoying it at home, we hope it keeps your favorite moments close every day.\n\nOn this page, you'll find setup, features, and support information. For tips, FAQs, and the latest updates, visit our Help Center.",
      "descriptionPC": "Thank you for choosing the FamilyAlbum™ Digital Frame.\nThis frame brings your family's memories to life by automatically syncing photos and videos from your FamilyAlbum™ app. Whether you're gifting it or enjoying it at home, we hope it keeps your favorite moments close every day.\n\nOn this page, you'll find setup, features, and support information. For tips, FAQs, and the latest updates, visit our Help Center.",
      "helpCenter": "Help Center"
    },
    "tableOfContents": {
      "quickStart": "Quick Start Guide",
      "basicOperation": "Basic Operation",
      "specifications": "Product Specifications",
      "legal": "Legal & Compliance Info",
      "safety": "Safety Information and Warnings",
      "warranty": "Warranty & Customer Support"
    },
    "section1": {
      "title": "Quick Start Guide",
      "intro": "Set up your frame in 4 easy steps\nWhat's in the box: frame, power cable, and support stand",
      "step1": {
        "title": "1. Plug in & Connect to Wi-Fi",
        "description": "Plug in the frame.\nFollow the on-screen steps to connect to Wi-Fi."
      },
      "step2": {
        "title": "2. Link Your FamilyAlbum™ Account",
        "description": "Scan the QR code on the frame or log in with your email and password.\nName your frame (e.g., Living Room, Grandma's House).\nDon't have the app yet? Download FamilyAlbum™ from the App Store or Google Play.",
        "downloadLink": "Download from HERE"
      },
      "step3": {
        "title": "3. Choose What to Display",
        "description": "Select your album(s) and which photos will appear on your frame.\n(You can change this anytime from Settings.)"
      },
      "step4": {
        "title": "4. Enjoy Automatic Syncing!",
        "description": "Your frame syncs automatically with the FamilyAlbum™ app.\nSit back and enjoy your memories."
      },
      "contact": {
        "title": "Need help?",
        "helpCenter": "Help Center",
        "contactLabel": "Contact:"
      }
    },
    "section2": {
      "title": "Basic Operation",
      "overview": {
        "description": "To use the FamilyAlbum™ Digital Frame, you'll need the FamilyAlbum™ app and a Wi-Fi connection.\nYou can adjust frame settings - such as layout, slideshow duration, and sleep mode schedule - remotely from the app at any time.\nIf you're already using FamilyAlbum™, simply connect your account during setup. Your photos will sync automatically for a smooth, hands-free experience.\nIf you're new to FamilyAlbum™, download FamilyAlbum™ from the App Store or Google Play to create your account."
      },
      "powerOn": {
        "title": "Power & On/Off",
        "description": "Plug in your frame using only the included power cable.\nThe frame will power on automatically and display the setup screen.\nThe power port (A) and power button (B) are located on the back of the frame.",
        "diagram": "See the image below to identify the correct input."
      },
      "powerOff": {
        "title": "To turn the frame off:",
        "description": "Press the power button (B) on the back of the frame to turn it off."
      },
      "operation": {
        "title": "Using the Frame",
        "description": "The FamilyAlbum™ Digital Frame features a simple, intuitive touchscreen interface.\nYou can tap the screen to take an action, or swipe left or right to browse through photos.\nAfter the initial setup, we recommend using the FamilyAlbum™ app to manage frame settings.\nThe app offers a smooth and convenient way to personalize your frame - including layout, slideshow duration, sleep schedule, and more."
      },
      "wifi": {
        "title": "Connect to Wi-Fi",
        "description": "Tap through the on-screen prompts to select your Wi-Fi network.\nEnter the password using the on-screen keyboard.\nOnce successfully connected, you can continue.\nAfter connecting to Wi-Fi, the frame may automatically install software updates to maintain performance and security.",
        "noteTitle": "Note:",
        "note": "This frame supports 2.4 GHz Wi-Fi networks only (IEEE 802.11 b/g/n). 5GHz networks are not supported.\nAn active Internet connection is required. If your frame is offline for more than 7 days, photos from the app will no longer be displayed.\nPlease keep your frame connected to Wi-Fi at all times for the best experience."
      },
      "account": {
        "title": "Link to Your FamilyAlbum™ Account",
        "description": "Follow the on-screen instructions to connect your frame to your FamilyAlbum™ account.\nYou have two options:",
        "option1Title": "1. Using a QR Code",
        "option1": "Scan the QR code displayed on the frame with the smartphone that has the FamilyAlbum™ app installed.\nCheck that the confirmation code shown on your frame matches the one displayed in a smartphone, then tap \"Confirm\" in the app. Once approved, the frame's screen will automatically switch to the next step.",
        "option2Title": "2. Using Email and Password",
        "option2": "Enter your email address and password directly on the frame.\nYou'll receive a confirmation code by email to complete two-factor authentication.\nDon't have the FamilyAlbum™ app or an account yet?\nDownload \"FamilyAlbum\" from the App Store or Google Play, then create an account to get started.\nFor more guidance on using the app, visit: https://help.family-album.com/hc/en-us"
      },
      "mediaSelection": {
        "title": "Media Selection",
        "description": "You can control what photos sync to your frame from your FamilyAlbum™ account.\nDuring setup, you'll choose how you'd like photos to appear - based on your preferences.\nTo make changes later, use the FamilyAlbum™ app.",
        "note": "From the app, you can adjust what albums or photo types are displayed on the frame - anytime, from anywhere."
      },
      "displaySettings": {
        "title": "Display Settings",
        "description": "Tap the frame screen once to bring up Frame Settings.\nFrom here, you can personalize features like slideshow speed, display order, clock display, and more.\nFor added flexibility, you can also manage these settings remotely from the FamilyAlbum™ app."
      },
      "sleep": {
        "title": "Sleep Mode",
        "description": "Configure a sleep schedule so the screen turns off at night and wakes up in the morning.\nOptionally display the clock during sleep mode - or leave the screen completely dark.\nTo wake the frame, simply tap the screen.\nSleep mode settings can also be managed anytime via the FamilyAlbum™ app."
      },
      "reset": {
        "title": "Factory Reset",
        "intro": "If you need to return your frame to its original settings, you can perform a factory reset in one of three ways:",
        "description": "From the Frame Settings: Tap the screen to open the settings menu, select Factory Reset, then follow the on-screen instructions.\nUsing the Power Button: Long-press the power button until the reset screen appears. Confirm to complete the factory reset.\nFrom the FamilyAlbum™ app: You can also reset your frame remotely via the Settings tab in the app.",
        "noteTitle": "Important:",
        "note": "Your frame must be connected to the Internet to function properly. Note: A factory reset will erase all frame settings and unlink your FamilyAlbum™ account.\nOnly perform this if you're troubleshooting or preparing the frame for someone else."
      }
    },
    "section3": {
      "title": "Product Specifications",
      "productName": {
        "title": "Product Name",
        "value": "FamilyAlbum™ Digital Frame"
      },
      "productModel": {
        "title": "Product Model",
        "value": "MDP01"
      },
      "display": {
        "title": "Display",
        "value": "10.1″ IPS touchscreen (1280 × 800 resolution, 16:10)"
      },
      "wifi": {
        "title": "Wi-Fi Only",
        "value": "IEEE 802.11 b/g/n (2.4 GHz only)\nBluetooth not supported"
      },
      "ports": {
        "title": "Connectivity & Ports",
        "value": "USB-C port (Not available for use)\nSD card slot (Not available for use)\nDC power input"
      },
      "storage": {
        "title": "Storage & Media Support",
        "value": "32 GB internal storage (approx. enough for ~25,000 photos)\nSupports standard photo, video, and audio formats"
      }
    },
    "section4": {
      "title": "Legal & Compliance Info",
      "intendedUse": {
        "title": "Intended Use",
        "description": "This product is designed for indoor, non-commercial use only. Outdoor operation is prohibited.\nNo vehicular use. The device enclosure is not weatherized.\nDo not open, modify, or use non-approved accessories; use only the included power adapter.\nDo not use the frame in damp or wet environments.\nFailure to follow any of these instructions may create safety hazards and void warranty coverage.\nThis product requires an active internet connection and may automatically receive software updates to maintain performance and security."
      },
      "environment": {
        "title": "Environmental Compliance",
        "description": "Please dispose of this device and its components in accordance with local electronic waste regulations.\nDo not dispose of this product in household trash. Look for local collection points or return programs."
      },
      "manufacturer": {
        "title": "Distributor・Manufacturer Information",
        "description": "Distributor:\nMIXI, Inc. [Shibuya Scramble Square 36F, 2-24-12 Shibuya, Shibuya-ku, Tokyo, 150-6136, Japan]\n\nManufacturer:\nJENESIS COMPANY LIMITED [7th PMO Bldg., 2-8-1Kanda Tsukasamachi, Chiyoda-ku, Tokyo 101-0048, Japan]"
      }
    },
    "section5": {
      "title": "Safety Information and Warnings",
      "overview": {
        "description": "Please read and follow these safety instructions to ensure proper use of your FamilyAlbum™ Digital Frame.\n\n⚠️ WARNING: Failure to follow these safety instructions may result in electric shock, fire, or damage to the device. Damage caused by misuse or unauthorized modifications is not covered under warranty."
      },
      "childSafety": {
        "title": "Baby & Child Safety",
        "description": "This product is not a toy. Keep the frame and all accessories, including power cables and packaging materials, out of reach of babies and young children.\nNever place the frame within or near a child's sleeping area, or anywhere a child can reach, pull, or tip it over.\nKeep power cables secured and well away from crawling or walking paths to prevent tripping, pulling, chewing, or entanglement.\nPlastic packaging materials and small detachable parts may pose a choking or suffocation hazard. Dispose of packaging immediately and keep all loose components away from children.\nUse the frame only under adult supervision. Do not leave it where a child could climb on furniture to reach it or risk injury from falling objects.",
        "note": "⚠️ Improper placement or use of this product near children may result in injury or damage. Please follow all safety guidelines carefully."
      },
      "electricalSafety": {
        "title": "Electrical Safety",
        "description": "Use only the supplied power adapter.\nInspect the cord and plug before each use. Do not use it if the power cord or plug appears damaged.\nKeep the power adapter away from heat sources, liquids, or high humidity.\nUnplug the frame during thunderstorms or extended periods of non-use.\nDo not cover the adapter, power brick, or ventilation openings.\nProvide ventilation; keep at least 2 inches clearance around the device and adapter.\nDo not place the adapter on soft or flammable materials (e.g., bedding, carpets, curtains). Use on a hard, stable, non-flammable surface.\nDo not overload outlets or use loose sockets, multi-plug cubes, or questionable extension cords. \nUnplug during long periods of non-use or if you detect smoke, odor, unusual heat, or noise."
      },
      "fireSafety": {
        "title": "Fire & Heat Warning",
        "description": "Do not block ventilation openings.\nKeep the frame away from direct sunlight, heat vents, or flammable materials.\nDo not cover the frame with cloth or paper while in use."
      },
      "moistureSafety": {
        "title": "Water and Moisture",
        "description": "This product is for indoor use only.\nDo not expose the frame to rain, damp areas, or excessive humidity.\nAvoid placing liquids near the device.\nIf moisture enters the frame, unplug it immediately and contact support."
      },
      "maintenance": {
        "title": "Maintenance and Cleaning",
        "description": "Unplug the power adapter from the wall outlet before any maintenance or cleaning.\nDo not attempt to disassemble or repair the frame yourself.\nClean the screen using a soft, dry cloth only.\nDo not use water, cleaning sprays, solvents, or abrasives."
      }
    },
    "section6": {
      "title": "Warranty & Customer Support",
      "warranty": {
        "title": "Limited Warranty",
        "description": "This product is covered by a 1-year limited warranty from the date of original retail purchase by the first end-user. The warranty covers defects in materials and workmanship under normal, intended use.\nIf a defect arises during the warranty period, we’ll repair or replace the frame at our option. Proof of purchase may be required. THE FOREGOING REPAIR OR REPLACEMENT IS YOUR SOLE AND EXCLUSIVE REMEDY FOR ANY NONCONFORMITY OR DEFECT IN THE PRODUCT."
      },
      "warrantyExclusions": {
        "title": "Exclusions",
        "intro": "The warranty does not cover:",
        "exclusions": "Damage caused by misuse, accidents, neglect, improper installation, or unauthorized modifications\nDamage from non-approved accessories, power surges, liquids, or exposure to moisture\nDamage from liquids, power surges, or improper ventilation\nCosmetic damage and wear and tear\nProducts not purchased through authorized sellers",
        "note": "Damage resulting from failure to follow safety instructions may void your warranty."
      },
      "support": {
        "title": "Customer Support",
        "description": "If you have any questions or issues with the product, please contact us below. Our support team will help with setup, troubleshooting, and warranty questions.",
        "helpCenter": "Help Center",
        "contactLabel": "Contact:"
      }
    }
  }
}
